import { API_URLS } from '@chessx/shared/api-routes'
import React, { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { validateEmail } from '../../libraries/common'
import useUserStore from '../../store/UserStore'
import axiosInstance from '../../network/axiosInstance'
import { STORAGE_KEYS } from '../../constants'
import { toastErrorMixin } from '../../utils/interact'
import { EmailIcon, EyeIcon, EyeClosedIcon, LockIcon } from '../../assets/icons'
import './AdminLogin.scss'

interface AdminLoginForm {
  email: string
  password: string
}

interface AdminLoginErrors {
  email?: string | null
  password?: string | null
}

const AdminLogin = () => {
  const navigate = useNavigate()
  const { setUser, setUserId, setToken, setRefreshToken } = useUserStore()
  const [form, setForm] = useState<AdminLoginForm>({ email: '', password: '' })
  const [errors, setErrors] = useState<AdminLoginErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [passwordShown, setPasswordShown] = useState(false)

  const setField = (field: keyof AdminLoginForm, value: string) => {
    setForm({ ...form, [field]: value })
    if (errors[field as keyof AdminLoginErrors]) setErrors({ ...errors, [field]: null })
  }

  const findFormErrors = (): AdminLoginErrors => {
    const { email, password } = form
    const newErrors: AdminLoginErrors = {}
    if (!email || email === '') newErrors.email = 'Email cannot be blank!'
    else if (!validateEmail(email)) newErrors.email = 'Email should be valid!'
    if (!password || password === '') newErrors.password = 'Password cannot be blank!'
    else if (password.length < 7)
      newErrors.password = 'Password should be at least 8 characters long!'
    return newErrors
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const newErrors = findFormErrors()
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    setSubmitting(true)
    try {
      const url = API_URLS.LOGIN
      const res = await axiosInstance.post(url, form)
      if (res && res.data && res.data.status === 200) {
        const userData = res.data.data
        if (userData.role !== 'admin') {
          toastErrorMixin.fire({ title: 'Access denied. Admin only.' })
          return
        }
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData))
        setUser(userData)
        setUserId(userData._id)
        setToken(userData.accessToken)
        setRefreshToken(userData.refreshToken)
        navigate('/admin/dashboard')
      } else {
        toastErrorMixin.fire({ title: res.data.message || 'Login failed.' })
      }
    } catch (err) {
      toastErrorMixin.fire({ title: 'Error logging in. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <div className="admin-login-logo">
            <span className="logo-icon">♛</span>
          </div>
          <h1 className="admin-login-title">ChessX Admin</h1>
          <p className="admin-login-subtitle">Sign in to your admin panel</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form" noValidate>
          <div className="form-field">
            <label className="field-label">Email Address</label>
            <div className={`field-input-wrap ${errors.email ? 'is-invalid' : ''}`}>
              <span className="field-icon">
                <EmailIcon height={18} width={18} fill="#64748b" />
              </span>
              <input
                type="email"
                className="field-input"
                placeholder="admin@chessx.com"
                value={form.email}
                onChange={e => setField('email', e.target.value)}
                autoComplete="email"
              />
            </div>
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-field">
            <label className="field-label">Password</label>
            <div className={`field-input-wrap ${errors.password ? 'is-invalid' : ''}`}>
              <span className="field-icon">
                <LockIcon height={18} width={18} fill="#64748b" />
              </span>
              <input
                type={passwordShown ? 'text' : 'password'}
                className="field-input"
                placeholder="Enter your password"
                value={form.password}
                onChange={e => setField('password', e.target.value)}
                autoComplete="current-password"
              />
              <span className="field-icon-right" onClick={() => setPasswordShown(s => !s)}>
                {passwordShown ? (
                  <EyeClosedIcon height={18} width={18} fill="#64748b" />
                ) : (
                  <EyeIcon height={18} width={18} fill="#64748b" />
                )}
              </span>
            </div>
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <button type="submit" className="login-btn" disabled={submitting}>
            {submitting ? <Spinner size="sm" animation="border" /> : 'Sign In'}
          </button>
        </form>

        <p className="admin-login-footer">ChessX Admin Panel &copy; {new Date().getFullYear()}</p>
      </div>
    </div>
  )
}

export default AdminLogin
